/* eslint-disable react/function-component-definition */
import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/fontawesome-free-solid';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Row, Col, Card, Badge, Image } from 'react-bootstrap';
import axios from 'axios';
import styles from './suggestMergeModal.module.scss';
import { TableContext } from '../../contexts/tableContext';
import { Entry } from '../../redux/entrySlice';
import SelectBox from '../../components/SelectBox/SelectBox';
import TextArea from '../../components/TextArea/TextArea';
import { REACT_APP_API_CAFES_DROPDOWN } from '../../redux/apiConstants';
import Loader from '../../components/Loader/Loader';

const SuggestMergeModal = React.memo(({ show, handleClose, sourceCafeId, sourceCafe, targetCafe, onSuccess }) => {
  const { formthemeStyle, errormsgStyle } = useContext(TableContext);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [cafes, setCafes] = useState([]);
  const [loadingCafes, setLoadingCafes] = useState(false);
  const [selectedSourceCafe, setSelectedSourceCafe] = useState(null);
  const [selectedTargetCafe, setSelectedTargetCafe] = useState(null);
  const dispatch = useDispatch();

  // Check if both cafes are pre-selected (from Find Similar flow)
  const isPreSelected = !!(sourceCafe && targetCafe);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();

  useEffect(() => {
    if (show) {
      // If cafes are pre-selected, use them directly
      if (isPreSelected) {
        setSelectedSourceCafe(sourceCafe);
        setSelectedTargetCafe(targetCafe);
      } else {
        // Otherwise load cafes for dropdown selection
        getCafes();
        if (sourceCafeId) {
          fetchCafeDetails(sourceCafeId, 'source');
        }
      }
    }
  }, [show, sourceCafeId, sourceCafe, targetCafe]);

  const getCafes = async () => {
    setLoadingCafes(true);
    try {
      const response = await axios.get(REACT_APP_API_CAFES_DROPDOWN);
      setCafes(response.data?.data?.cafes?.selectdata || []);
    } catch (err) {
      console.error('Error fetching cafes:', err);
      toast.error('Failed to load cafes');
    } finally {
      setLoadingCafes(false);
    }
  };

  const fetchCafeDetails = async (cafeId, type) => {
    try {
      const actionData = {
        actionUrl: `cafe/${cafeId}`,
        actionMethod: 'get',
      };
      const resp = await dispatch(Entry(actionData));
      if (resp.payload?.data?.cafe) {
        if (type === 'source') {
          setSelectedSourceCafe(resp.payload.data.cafe);
        } else {
          setSelectedTargetCafe(resp.payload.data.cafe);
        }
      }
    } catch (err) {
      console.error('Error fetching cafe details:', err);
    }
  };

  const watchSourceCafe = watch('sourceCafe');
  const watchTargetCafe = watch('targetCafe');

  useEffect(() => {
    if (watchSourceCafe) {
      const cafeId = watchSourceCafe.id || watchSourceCafe;
      if (cafeId && cafeId !== selectedSourceCafe?._id) {
        fetchCafeDetails(cafeId, 'source');
      }
    }
  }, [watchSourceCafe]);

  useEffect(() => {
    if (watchTargetCafe) {
      const cafeId = watchTargetCafe.id || watchTargetCafe;
      if (cafeId && cafeId !== selectedTargetCafe?._id) {
        fetchCafeDetails(cafeId, 'target');
      }
    }
  }, [watchTargetCafe]);

  const onSubmitdata = (data) => {
    setSubmitting(true);
    setError(null);

    // Get cafe IDs - prefer pre-selected, then form data
    const sourceCafeIdValue = sourceCafe?._id || data.sourceCafe?.id || data.sourceCafe || sourceCafeId;
    const targetCafeIdValue = targetCafe?._id || data.targetCafe?.id || data.targetCafe;

    // Validation
    if (!sourceCafeIdValue || !targetCafeIdValue) {
      toast.error('Please select both source and target cafes');
      setSubmitting(false);
      return;
    }

    if (sourceCafeIdValue === targetCafeIdValue) {
      toast.error('Source and target cafe cannot be the same');
      setSubmitting(false);
      return;
    }

    // Check if source cafe has vendor
    if (!selectedSourceCafe?.vendor) {
      toast.error('Source cafe must have a vendor to notify');
      setSubmitting(false);
      return;
    }

    const apiData = {
      sourceCafeId: sourceCafeIdValue,
      targetCafeId: targetCafeIdValue,
      adminNotes: data.adminNotes || '',
    };

    const actionData = {
      actionUrl: 'cafe/merge-suggestion',
      actionMethod: 'post',
      apiData: apiData,
    };

    dispatch(Entry(actionData)).then((resp) => {
      setSubmitting(false);
      // Accept both 200 and 201 as success codes
      if (resp.payload.code === 200 || resp.payload.code === 201 || resp.payload.success === true) {
        toast.success('Merge suggestion sent to vendor successfully');
        reset();
        handleClose();
        if (onSuccess) onSuccess();
      } else if (resp.payload.code === 409) {
        toast.warning('A merge request already exists for these cafes');
        setError(resp.payload.message);
      } else if (resp.payload.code === 400) {
        toast.error(resp.payload.message || 'Invalid request');
        setError(resp.payload.message);
      } else {
        toast.error('Failed to create merge suggestion');
        setError(resp.payload.message);
      }
    });
  };

  const renderCafePreview = (cafe, type) => {
    if (!cafe) return null;

    return (
      <Card className={styles.cafePreviewCard}>
        <Card.Body>
          <Row>
            <Col xs={4} className={styles.imageCol}>
              {cafe.image ? (
                <Image src={cafe.image} alt={cafe.name} className={styles.previewImage} fluid rounded />
              ) : (
                <div className={styles.placeholderImage}>
                  <span>{cafe.name?.charAt(0) || '?'}</span>
                </div>
              )}
            </Col>
            <Col xs={8}>
              <h6 className={styles.previewCafeName}>{cafe.name}</h6>
              <p className={styles.previewAddress}>{cafe.address || 'No address'}</p>
              <div className={styles.previewBadges}>
                <Badge bg={type === 'source' ? 'primary' : 'success'}>{cafe.cafeStatus}</Badge>
                {cafe.isClaimed && <Badge bg="info">Claimed</Badge>}
                {cafe.vendor && (
                  <Badge bg="secondary">
                    <small>Vendor: {cafe.vendor.name}</small>
                  </Badge>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  if (!show) return null;

  return (
    <div className={styles.popupbox}>
      <div className={styles.box}>
        <button className={styles.closeicon} aria-label="Close" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <h2 className={styles.heading}>Suggest Cafe Merge</h2>
        <p className={styles.subtitle}>
          Suggest a merge to a vendor. They will be notified and can accept or reject the suggestion.
        </p>

        <div className="container-fluid mt-3">
          {loadingCafes && !isPreSelected ? (
            <div className={styles.loadingContainer}>
              <Loader />
              <p>Loading cafes...</p>
            </div>
          ) : isPreSelected ? (
            /* Simplified confirmation view when both cafes are pre-selected */
            <form onSubmit={handleSubmit(onSubmitdata)}>
              <div className={styles.confirmationView}>
                <Row>
                  <Col lg={6} md={12} className="mb-3">
                    <label className={styles.previewLabel}>
                      <i className="fa fa-arrow-right" style={{ color: '#39979d', marginRight: '8px' }} />
                      Source Cafe (Vendor's Cafe)
                    </label>
                    {renderCafePreview(selectedSourceCafe, 'source')}
                  </Col>
                  <Col lg={6} md={12} className="mb-3">
                    <label className={styles.previewLabel}>
                      <i className="fa fa-arrow-left" style={{ color: '#28a745', marginRight: '8px' }} />
                      Target Cafe (Merge Into)
                    </label>
                    {renderCafePreview(selectedTargetCafe, 'target')}
                  </Col>
                </Row>

                <div className={styles.mergeArrow}>
                  <i className="fa fa-long-arrow-alt-right" />
                  <span>will be suggested to merge into</span>
                  <i className="fa fa-long-arrow-alt-right" />
                </div>

                <Row className="mt-3">
                  <Col lg={12}>
                    <TextArea
                      className={styles.inputbox}
                      classNamedark={styles.inputbox1}
                      labelstyle={styles.formlabel}
                      label="Notes for Vendor (Optional)"
                      fieldName="adminNotes"
                      placeHolder="Add any notes for the vendor about this merge suggestion..."
                      register={register}
                      errors={errors}
                      maximLength={500}
                      minimLength={0}
                      rows={3}
                    />
                  </Col>
                </Row>
              </div>

              <div className="d-flex justify-content-end mt-4 gap-2">
                <input
                  className={styles.resetbtn}
                  type="button"
                  value="Cancel"
                  onClick={handleClose}
                />
                <input
                  className={styles.formbtn}
                  type="submit"
                  disabled={submitting}
                  value={submitting ? 'Sending...' : 'Send Suggestion to Vendor'}
                />
              </div>

              {error && (
                <h6 className={styles[errormsgStyle]} style={{ textAlign: 'center', marginTop: '10px' }}>
                  {error}
                </h6>
              )}
            </form>
          ) : (
            /* Full form with dropdowns when selecting manually */
            <form onSubmit={handleSubmit(onSubmitdata)}>
              <Row>
                <Col lg={12} md={12} sm={12}>
                  <SelectBox
                    className={styles.inputbox}
                    classNamedark={styles.inputbox1}
                    name="sourceCafe"
                    label="Source Cafe (Vendor's Cafe)"
                    labelstyle={styles.formlabel}
                    control={control}
                    register={register}
                    values={cafes.filter((c) => c.isClaimed)}
                    errors={errors}
                    placeholder="Select source cafe with vendor..."
                    defaultvalue={sourceCafeId}
                    mandatory
                    isRequired
                  />
                </Col>
              </Row>

              {selectedSourceCafe && (
                <Row>
                  <Col lg={12}>
                    <label className={styles.previewLabel}>Source Cafe Preview:</label>
                    {renderCafePreview(selectedSourceCafe, 'source')}
                  </Col>
                </Row>
              )}

              <Row className="mt-3">
                <Col lg={12} md={12} sm={12}>
                  <SelectBox
                    className={styles.inputbox}
                    classNamedark={styles.inputbox1}
                    name="targetCafe"
                    label="Target Cafe (Merge Into)"
                    labelstyle={styles.formlabel}
                    control={control}
                    register={register}
                    values={cafes}
                    errors={errors}
                    placeholder="Select target cafe..."
                    mandatory
                    isRequired
                  />
                </Col>
              </Row>

              {selectedTargetCafe && (
                <Row>
                  <Col lg={12}>
                    <label className={styles.previewLabel}>Target Cafe Preview:</label>
                    {renderCafePreview(selectedTargetCafe, 'target')}
                  </Col>
                </Row>
              )}

              <Row className="mt-3">
                <Col lg={12} md={12} sm={12}>
                  <TextArea
                    className={styles.inputbox}
                    classNamedark={styles.inputbox1}
                    labelstyle={styles.formlabel}
                    label="Notes for Vendor (Optional)"
                    fieldName="adminNotes"
                    placeHolder="Explain why you're suggesting this merge (optional)..."
                    register={register}
                    errors={errors}
                    maximLength={500}
                    minimLength={0}
                    rows={4}
                  />
                  <small className={styles.helperText}>
                    These notes will be visible to the vendor when they review the suggestion.
                  </small>
                </Col>
              </Row>

              <div className="d-flex justify-content-end mt-4 gap-2">
                <input
                  className={styles.resetbtn}
                  type="button"
                  value="Cancel"
                  onClick={handleClose}
                />
                <input
                  className={styles.formbtn}
                  type="submit"
                  disabled={submitting}
                  value={submitting ? 'Sending...' : 'Send Suggestion'}
                />
              </div>

              {error && (
                <h6 className={styles[errormsgStyle]} style={{ textAlign: 'center', marginTop: '10px' }}>
                  {error}
                </h6>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
});

export default SuggestMergeModal;
